import React from "react";
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Thermometer, Waves, Users, Wrench, Package, Shield, Lightbulb, MessageSquare, Globe, TreePine, Home, PartyPopper, Bus, User } from "lucide-react";
import Logo from "@/components/Logo";

const WhyKit = () => {
  return (
    <div className="min-h-screen bg-sauna-beige py-12 px-4 sm:px-6 md:px-8 lg:px-12">
      <Helmet>
        <title>Why We're Building the SaunaDip Kit | SaunaDip</title>
        <meta name="description" content="Discover why we're building the SaunaDip Kit – a simple, beautiful temperature monitoring system for wild wellness experiences. Learn about our mission, the problems we solve, and our vision for the future of sauna and cold plunge." />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <Logo size="lg" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-sauna-navy mb-6">
            Why We're Building the SaunaDip Kit
          </h1>
          <div className="h-1 w-24 bg-gradient-to-r from-sauna-orange to-sauna-blue rounded-full mx-auto mb-6"></div>
          <p className="text-xl text-sauna-navy/80 max-w-2xl mx-auto">
            A simple, beautiful temperature monitoring system for wild wellness experiences.
          </p>
        </header>

        {/* SaunaDip Characters */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-sauna-navy mb-6 flex items-center gap-3">
            Meet the SaunaDip Characters: Sauna Salamander & Dip the Duck
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Sauna Salamander */}
            <Card className="bg-sauna-orange/10 border-sauna-orange/30 hover:shadow-lg transition-shadow duration-200">
              <CardContent className="flex flex-col items-center text-center py-8">
                <div className="bg-sauna-beige p-4 rounded-xl mb-4">
                  <img src="/assets/characters/sauna-salamander.png" alt="Sauna Salamander character illustration" className="w-40 h-auto" />
                  <div className="mt-2 text-lg font-mono text-sauna-orange/80">87°C</div>
                </div>
                <h3 className="text-xl font-bold text-sauna-orange mb-2">Sauna Salamander</h3>
                <p className="text-base text-sauna-navy/80 mb-1 font-medium">Sensor + Display Unit (for the Sauna)</p>
                <p className="text-sauna-navy/70">
                  Place the Sauna Salamander display anywhere you like—inside or outside the sauna. For now, we recommend keeping it outside the sauna due to heat.
                </p>
              </CardContent>
            </Card>
            {/* Dip the Duck */}
            <Card className="bg-sauna-blue/10 border-sauna-blue/30 hover:shadow-lg transition-shadow duration-200">
              <CardContent className="flex flex-col items-center text-center py-8">
                <div className="bg-sauna-beige p-4 rounded-xl mb-4">
                  <img src="/assets/characters/dip-the-duck.png" alt="Dip the Duck character illustration" className="w-40 h-auto" />
                  <div className="mt-2 text-lg font-mono text-sauna-blue/80">6°C</div>
                </div>
                <h3 className="text-xl font-bold text-sauna-blue mb-2">Dip the Duck</h3>
                <p className="text-base text-sauna-navy/80 mb-1 font-medium">Floating Plunge Sensor (for the Cold Dip)</p>
                <p className="text-sauna-navy/70">
                  Dip floats calmly in your plunge pool, broadcasting the water temperature every few minutes via Bluetooth. Always chill. Always watching. Never asks you to download an app.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Product Purpose */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-sauna-blue/10 to-sauna-orange/10 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-semibold text-sauna-navy mb-4 flex items-center gap-3">
              <Thermometer className="w-6 h-6 text-sauna-orange" />
              Product Purpose
            </h2>
            <p className="text-lg text-sauna-navy/80 mb-6">
              SaunaDip is a two-part sensor system designed for wild wellness experiences:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/80 hover:shadow-lg transition-shadow duration-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-1">
                    <Thermometer className="w-6 h-6 text-sauna-orange" />
                    <h3 className="font-bold text-sauna-orange text-lg">Wall Unit</h3>
                  </div>
                  <div className="text-sm text-sauna-navy/70 mb-3">Place anywhere (recommended outside sauna for now)</div>
                  <p className="text-sauna-navy/70">
                    A wall-mounted display showing real-time temperature and humidity in your sauna.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white/80 hover:shadow-lg transition-shadow duration-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-1">
                    <Waves className="w-6 h-6 text-sauna-orange" />
                    <h3 className="font-bold text-sauna-blue text-lg">DipSense Duck</h3>
                  </div>
                  <div className="text-sm text-sauna-navy/70 mb-3">Floats in your plunge</div>
                  <p className="text-sauna-navy/70">
                    A floating plunge sensor that transmits cold water temperature wirelessly via BLE.
                  </p>
                </CardContent>
              </Card>
            </div>
            <p className="mt-6 text-sauna-navy/80 italic">
              "We believe wild wellness deserves tools that are simple, beautiful, and invisible when they need to be — no apps, no accounts, no nonsense. Just clarity in the heat and chill."
            </p>
          </div>
        </section>

        {/* Problem & Solution */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-sauna-navy mb-6 flex items-center gap-3">
            <Lightbulb className="w-6 h-6 text-sauna-orange" />
            The Problem We're Solving
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium text-sauna-navy mb-4">Current Challenges</h3>
                <ul className="space-y-3 text-sauna-navy/80">
                  <li className="flex items-start gap-2">
                    <span className="text-sauna-orange">•</span>
                    <span>Guess the heat or use unreliable thermometers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sauna-orange">•</span>
                    <span>Have no plunge pool temp readings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sauna-orange">•</span>
                    <span>Run too hot or too cold without real accountability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sauna-orange">•</span>
                    <span>Waste staff time checking and resetting analog gauges</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sauna-orange">•</span>
                    <span>Miss a chance to delight users with confidence and clarity</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium text-sauna-navy mb-4">Our Solution</h3>
                <p className="text-sauna-navy/80 mb-4">
                  SaunaDip gives operators a clean, modern way to display live heat and cold data — visible to guests and trusted by staff.
                </p>
                <div className="bg-sauna-blue/5 rounded-lg p-4">
                  <p className="text-sm text-sauna-navy/70">
                    "The moment I saw both temperatures on one display, I knew this would transform how we run our sessions."
                  </p>
                  <p className="text-sm text-sauna-navy/60 mt-2">— Early tester, Wild Atlantic Sauna</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Target Users */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-sauna-navy mb-6 flex items-center gap-3">
            <Users className="w-6 h-6 text-sauna-orange" />
            Who It's For
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                title: "Independent Operators",
                description: "Especially wild, mobile, or outdoor setups",
                icon: <Globe className="w-6 h-6 text-sauna-orange" />
              },
              {
                title: "Retreat Centers",
                description: "With plunge tubs and wellness programs",
                icon: <TreePine className="w-6 h-6 text-sauna-orange" />
              },
              {
                title: "Wellness Seekers",
                description: "Setting up saunas or ice baths at home",
                icon: <Home className="w-6 h-6 text-sauna-orange" />
              },
              {
                title: "Event Hosts",
                description: "Who want to add magic + monitoring",
                icon: <PartyPopper className="w-6 h-6 text-sauna-orange" />
              },
              {
                title: "Mobile Operators",
                description: "Bringing sauna and plunge experiences on the go",
                icon: <Bus className="w-6 h-6 text-sauna-orange" />
              },
              {
                title: "Home Users",
                description: "For personal wellness and family fun",
                icon: <User className="w-6 h-6 text-sauna-orange" />
              }
            ].map((user, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-2">
                    {user.icon}
                    <h3 className="font-medium text-sauna-navy">{user.title}</h3>
                  </div>
                  <p className="text-sm text-sauna-navy/70">{user.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Development Progress */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-sauna-navy mb-6 flex items-center gap-3">
            <Wrench className="w-6 h-6 text-sauna-orange" />
            Development Progress
          </h2>
          <div className="space-y-6">
            {[
              { stage: "Product Concept", status: "complete", progress: 100 },
              { stage: "Hardware Design", status: "complete", progress: 100 },
              { stage: "Component Sourcing", status: "in-progress", progress: 75 },
              { stage: "Local Manufacturing", status: "in-progress", progress: 60 },
              { stage: "Firmware Development", status: "in-progress", progress: 80 },
              { stage: "User Testing", status: "planned", progress: 20 },
              { stage: "Shipping & Fulfillment", status: "in-progress", progress: 40 }
            ].map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sauna-navy">{item.stage}</span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold text-white
                      ${item.status === "complete" ? "bg-sauna-green" : ""}
                      ${item.status === "in-progress" ? "bg-sauna-orange" : ""}
                      ${item.status === "planned" ? "bg-sauna-blue" : ""}
                    `}
                  >
                    {item.status === "complete" ? "Complete" :
                     item.status === "in-progress" ? "In Progress" :
                     "Planned"}
                  </span>
                </div>
                <Progress value={item.progress} className="h-2" />
              </div>
            ))}
          </div>
        </section>

        {/* Research & Feedback */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-sauna-navy mb-6 flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-sauna-orange" />
            Research & Feedback
          </h2>
          <div className="bg-gradient-to-r from-sauna-blue/10 to-sauna-orange/10 rounded-2xl p-8">
            <p className="text-lg text-sauna-navy/80 mb-6">
              We've spoken with 10+ sauna operators, including wild setups along the Irish coast. They loved:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: "Unified Display",
                  description: "Seeing both sauna and plunge readings on one display",
                  icon: <Thermometer className="w-7 h-7 text-sauna-orange" />
                },
                {
                  title: "Clever Design",
                  description: "A chlorine-tab-holding duck (!)",
                  icon: <Lightbulb className="w-7 h-7 text-sauna-orange" />
                },
                {
                  title: "Simple Setup",
                  description: "No apps, no setup, no Wi-Fi required",
                  icon: <Lightbulb className="w-7 h-7 text-sauna-blue" />
                }
              ].map((feedback, index) => (
                <Card key={index} className="bg-white/80">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      {feedback.icon}
                      <h3 className="font-medium text-sauna-navy">{feedback.title}</h3>
                    </div>
                    <p className="text-sm text-sauna-navy/70">{feedback.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="mt-6 text-sauna-navy/80 italic">
              "They also helped shape our direction — one suggested offering a directory of high-integrity sauna setups, which we're launching alongside the hardware."
            </p>
            <p className="mt-4 text-sauna-navy/90 font-medium text-center">
              Want to stay in the loop? <a href="/blog" className="text-sauna-orange underline hover:text-sauna-blue">Read our blog</a> for upcoming updates on the SaunaDip kit!
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mb-16 text-center">
          <h2 className="text-3xl font-bold text-sauna-orange mb-4">Ready to Heat Things Up and Cool Down Right?</h2>
          <p className="text-lg text-sauna-navy/80 mb-6">Join the first wave of wild wellness pioneers bringing clarity and confidence to their sauna setups.</p>
          <a href="/choose-signup" className="inline-block px-8 py-3 bg-sauna-blue text-white rounded-lg font-semibold text-lg shadow hover:bg-sauna-orange transition-colors duration-200">Join SaunaDip</a>
        </section>

        {/* Scroll to Top Button */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 z-50 bg-sauna-orange text-white p-3 rounded-full shadow-lg hover:bg-sauna-blue transition-colors duration-200"
          aria-label="Scroll to top"
        >
          ↑
        </button>
      </div>
    </div>
  );
};

export default WhyKit; 